import type { GfeState, GfeSummary, GfeSwitchView, QualiphyExamView } from "@vaulted/shared";
import { GFE_LABELS } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post, put } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Field, Loading, Panel, PanelHeader, cx, formatDateTime } from "./ui";
import { MENU_ROW } from "./AvailabilityLight";

const TONE: Record<GfeState, "ok" | "wait" | "off" | "brand"> = {
  current: "ok",
  current_flagged: "wait",
  review: "wait",
  intake: "brand",
  pending: "brand",
  deferred: "brand",
  declined: "wait",
  not_done: "wait",
  needed: "wait",
};

const day = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/** "GFE ✓ (until Mar 8, 2027)", "GFE pending", "GFE needed", … for cards, the visit, and the profile. */
export function GfeBadge({ g, compact }: { g: GfeSummary | null; compact?: boolean }) {
  if (!g) return null;
  const label = g.state === "current" && g.until && !compact ? `${GFE_LABELS[g.state]} (until ${day(g.until)})` : GFE_LABELS[g.state];
  return compact ? (
    <span className={g.state === "current" ? "text-xs font-semibold text-ok" : "text-xs font-semibold text-wait"} title={g.flagNote ?? undefined}>
      {g.state === "current" ? "GFE ✓" : label}
    </span>
  ) : (
    <Badge tone={TONE[g.state]}>{label}</Badge>
  );
}

const RESULT: Record<NonNullable<QualiphyExamView["result"]>, string> = {
  approved: "Approved",
  rejected: "Declined",
  deferred: "Sent to Qualiphy's medical director",
  na: "Not done (ended without a decision)",
  missed: "Not done (didn't join the call)",
};

const examStatus = (e: QualiphyExamView) =>
  e.status === "failed"
    ? `Not sent: ${e.error}`
    : e.status === "cancelled"
      ? "Cancelled"
      : e.result
        ? RESULT[e.result]
        : e.status === "waiting"
          ? "Waiting on the client's intake (it leads into the screening)"
          : e.status === "sent"
            ? "Waiting on the client"
            : "Sending…";

function useGfeActions(clientId: string) {
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["gfe", clientId] });
    await qc.invalidateQueries({ queryKey: ["appointment"] });
    await qc.invalidateQueries({ queryKey: ["calendar"] });
  };
  return {
    send: useMutation({ mutationFn: (appointmentId: string | null) => post(`/api/clients/${clientId}/gfe/send`, { appointmentId }), onSuccess: done }),
    resend: useMutation({ mutationFn: (examId: string) => post(`/api/gfe/exams/${examId}/resend`, {}), onSuccess: done }),
    cancel: useMutation({ mutationFn: (examId: string) => post(`/api/gfe/exams/${examId}/cancel`, {}), onSuccess: done }),
    review: useMutation({
      mutationFn: (v: { examId: string; action: "flag" | "clear"; note?: string }) => post(`/api/gfe/exams/${v.examId}/review`, v.action === "flag" ? { action: "flag", note: v.note } : { action: "clear" }),
      onSuccess: done,
    }),
  };
}

/** Review an approval's provider notes: flag the restrictions (pre-filled with what the provider picked), or clear it. */
function ReviewControls({ clientId, examId, note }: { clientId: string; examId: string; note: string | null }) {
  const { review } = useGfeActions(clientId);
  const { can } = useSession();
  const [text, setText] = useState(note ?? "");
  if (!can("charts.write")) return null;
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-wait-wash p-3">
      <span className="text-sm font-semibold text-wait">The provider's approval has restrictions. Read them, then flag or clear.</span>
      <Field label="Restrictions (shown on the visit and chart)" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex gap-2">
        <Button variant="primary" busy={review.isPending} disabled={!text.trim()} onClick={() => review.mutate({ examId, action: "flag", note: text.trim() })}>
          Flag restrictions
        </Button>
        <Button variant="quiet" busy={review.isPending} onClick={() => review.mutate({ examId, action: "clear" })}>
          Clear (no restrictions)
        </Button>
      </div>
      {review.error && <Alert>{errorMessage(review.error)}</Alert>}
    </div>
  );
}

/** On the visit: the exam's state and what staff can do about it. */
export function GfeVisitSection({ g, clientId, appointmentId }: { g: GfeSummary; clientId: string; appointmentId: string }) {
  const { send, resend, cancel } = useGfeActions(clientId);
  const { can } = useSession();
  const error = send.error ?? resend.error ?? cancel.error;
  return (
    <section className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">Good faith exam</span>
        <GfeBadge g={g} />
        {g.source === "tag" && <span className="text-xs text-muted">Medical director</span>}
      </div>
      {g.state === "current_flagged" && g.flagNote && <p className="text-sm font-semibold whitespace-pre-wrap text-danger">{g.flagNote}</p>}
      {g.state === "review" && g.examId && <ReviewControls clientId={clientId} examId={g.examId} note={g.flagNote} />}
      {can("appointments.manage") && (
        <div className="flex flex-wrap gap-2">
          {(g.state === "needed" || g.state === "not_done" || g.state === "declined") && (
            <Button variant="secondary" busy={send.isPending} onClick={() => send.mutate(appointmentId)}>
              {g.state === "needed" ? "Send good faith exam" : "Send a new exam"}
            </Button>
          )}
          {(g.state === "pending" || g.state === "intake") && g.examId && (
            <>
              <Button variant="quiet" busy={resend.isPending} onClick={() => resend.mutate(g.examId!)}>
                {resend.isSuccess ? "Link sent" : g.state === "intake" ? "Resend forms link" : "Resend link"}
              </Button>
              <Button variant="quiet" busy={cancel.isPending} onClick={() => cancel.mutate(g.examId!)}>
                Cancel exam
              </Button>
            </>
          )}
        </div>
      )}
      {error && <Alert>{errorMessage(error)}</Alert>}
    </section>
  );
}

/** Client profile: every exam (Qualiphy and the medical director's tag), with results and PDFs. */
export function GfeTab({ clientId }: { clientId: string }) {
  const q = useQuery({ queryKey: ["gfe", clientId], queryFn: () => get<{ summary: GfeSummary; exams: QualiphyExamView[] }>(`/api/clients/${clientId}/gfe`) });
  const { send, resend, cancel } = useGfeActions(clientId);
  const { can } = useSession();
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <Alert>{errorMessage(q.error)}</Alert>;
  const { summary, exams } = q.data;
  const error = send.error ?? resend.error ?? cancel.error;
  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title="Good faith exam" description="A Qualiphy approval or the medical director's tag covers IV and IM visits for the clinic's approval period." />
        <div className="flex flex-col gap-3 px-5 pb-5">
          <div className="flex flex-wrap items-center gap-2">
            <GfeBadge g={summary} />
            {summary.source === "tag" && <span className="text-sm text-muted">From the medical director's tag</span>}
          </div>
          {summary.state === "current_flagged" && summary.flagNote && <p className="text-sm font-semibold whitespace-pre-wrap text-danger">{summary.flagNote}</p>}
          {summary.state === "review" && summary.examId && <ReviewControls clientId={clientId} examId={summary.examId} note={summary.flagNote} />}
          {can("appointments.manage") && summary.state !== "pending" && summary.state !== "deferred" && (
            <div>
              <Button variant="secondary" busy={send.isPending} onClick={() => send.mutate(null)}>
                Send good faith exam
              </Button>
            </div>
          )}
          {error && <Alert>{errorMessage(error)}</Alert>}
        </div>
      </Panel>
      <Panel>
        <PanelHeader title="Qualiphy exams" />
        {exams.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-muted">None yet.</p>
        ) : (
          <ul className="px-5 pb-3">
            {exams.map((e) => (
              <li key={e.id} className="flex flex-col gap-1.5 border-b border-mist py-3 text-sm last:border-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-semibold">{examStatus(e)}</span>
                  {e.qualiphyExamId && <span className="text-muted">Exam #{e.qualiphyExamId}</span>}
                  {e.test && <Badge tone="off">Test</Badge>}
                  {e.review === "flagged" && <Badge tone="wait">Restrictions</Badge>}
                  {e.goodUntil && <span className="text-muted">Good until {day(e.goodUntil)}</span>}
                  <span className="ml-auto text-muted">
                    Sent {formatDateTime(e.sentAt)} {e.via === "auto" ? "automatically" : e.sentBy ? `by ${e.sentBy}` : ""}
                    {e.teleState && e.teleState !== "TE" ? ` · for ${e.teleState}` : ""}
                  </span>
                </div>
                {e.resultAt && (
                  <span className="text-ink-soft">
                    {formatDateTime(e.resultAt)}
                    {e.providerName ? ` · ${e.providerName}` : ""}
                  </span>
                )}
                {e.reason && <p className="whitespace-pre-wrap text-ink-soft">{e.reason}</p>}
                {e.answersError && <p className="text-wait">The intake's answers didn't reach Qualiphy ({e.answersError}); the client answers those questions there.</p>}
                {e.linkSentAt && <span className="text-muted">Qualiphy link sent {formatDateTime(e.linkSentAt)}</span>}
                {e.flagNote && e.review !== "cleared" && <p className="whitespace-pre-wrap text-danger">{e.flagNote}</p>}
                <div className="flex flex-wrap gap-3">
                  {e.hasPdf && (
                    <a className="font-semibold text-brand underline" href={`/api/gfe/exams/${e.id}/pdf`} target="_blank" rel="noreferrer">
                      Visit PDF
                    </a>
                  )}
                  {e.questionsAnswers.length > 0 && (
                    <details>
                      <summary className="cursor-pointer font-semibold text-muted">Screening answers ({e.questionsAnswers.length})</summary>
                      <dl className="mt-1 grid gap-1">
                        {e.questionsAnswers.map((qa) => (
                          <div key={qa.no}>
                            <dt className="text-muted">{qa.question}</dt>
                            <dd>{qa.answer}</dd>
                          </div>
                        ))}
                      </dl>
                    </details>
                  )}
                  {(e.status === "sent" || e.status === "waiting") && can("appointments.manage") && (
                    <>
                      <button className="font-semibold text-brand" onClick={() => resend.mutate(e.id)}>
                        {e.status === "waiting" ? "Resend forms link" : "Resend link"}
                      </button>
                      <button className="font-semibold text-muted" onClick={() => cancel.mutate(e.id)}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/**
 * The Qualiphy auto-send switch, in the menu under the logo. Managers and admins turn it off
 * (Dr. Milligan on site, an event); it comes back on at midnight. Others just see that it's paused.
 */
export function GfeSwitch() {
  const qc = useQueryClient();
  const { can } = useSession();
  const q = useQuery({ queryKey: ["gfe-switch"], queryFn: () => get<GfeSwitchView>("/api/gfe/switch"), refetchInterval: 5 * 60_000 });
  const set = useMutation({
    mutationFn: (paused: boolean) => put<GfeSwitchView>("/api/gfe/switch", { paused }),
    onSuccess: (v) => qc.setQueryData(["gfe-switch"], v),
  });
  if (!q.data?.enabled) return null;
  const paused = !!q.data.pausedUntil;
  const label = paused ? "Qualiphy auto-send off until midnight" : "Qualiphy auto-send on";
  const hint = paused ? "Exams aren't sent automatically for new bookings. Turns back on at midnight." : "Exams are sent automatically when a visit that needs one is booked.";
  const text = (
    <span className="min-w-0 flex-1">
      <span className="block truncate">Qualiphy auto-send</span>
      {paused && <span className="block truncate text-xs text-[#fbbf24]">Off until midnight</span>}
    </span>
  );
  if (!can("screenings.pause")) return paused ? <div className={cx(MENU_ROW, "cursor-default hover:bg-transparent")} title={hint}>{text}</div> : null;
  return (
    <label className={cx(MENU_ROW, "cursor-pointer")} title={hint}>
      {text}
      <input type="checkbox" role="switch" aria-label="Qualiphy auto-send" className="peer sr-only" checked={!paused} disabled={set.isPending} onChange={(e) => set.mutate(!e.target.checked)} />
      <span className={cx("relative h-[18px] w-8 shrink-0 rounded-full transition peer-focus-visible:ring-2 peer-focus-visible:ring-white/40", paused ? "bg-white/20" : "bg-ok")}>
        <span className={cx("absolute top-0.5 size-3.5 rounded-full bg-white shadow transition", paused ? "left-0.5" : "left-[16px]")} />
      </span>
    </label>
  );
}
